import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  FileText,
  Calendar,
  Hash,
  ArrowLeft,
} from 'lucide-react';
import { Card, CardContent } from '@/app/components/ui/card';
import { Button } from '@/app/components/ui/button';
import { Badge } from '@/app/components/ui/badge';
import { Skeleton } from '@/app/components/ui/skeleton';
import { useGeneratedDocuments } from '@/hooks/use-setup-wizard';
import { GeneratedDocumentEditor } from './components/generated-document-editor';
import type { GeneratedComplianceDocument } from '@/lib/types';
import { useOrgTimeZone } from '@/hooks/use-settings';
import { formatDateTimeInTimeZone } from '@/lib/date-utils';

// ---------------------------------------------------------------------------
// Document type display config
// ---------------------------------------------------------------------------

const DOC_TYPE_CONFIG: Record<string, { label: string; color: string }> = {
  rope_access_procedure: { label: 'Rope Access Procedure', color: 'bg-blue-100 text-blue-800' },
  safety_policy: { label: 'Safety Policy', color: 'bg-green-100 text-green-800' },
  nominated_personnel_register: { label: 'Nominated Personnel Register', color: 'bg-purple-100 text-purple-800' },
  roles_responsibilities: { label: 'Roles & Responsibilities', color: 'bg-indigo-100 text-indigo-800' },
  legal_register: { label: 'Legal Register', color: 'bg-amber-100 text-amber-800' },
  non_legal_register: { label: 'Non-Legal Register', color: 'bg-orange-100 text-orange-800' },
  internal_audit_schedule: { label: 'Internal Audit Schedule', color: 'bg-teal-100 text-teal-800' },
  management_review_template: { label: 'Management Review Record', color: 'bg-rose-100 text-rose-800' },
  documents_and_records_procedure: { label: 'Documents & Records Procedure', color: 'bg-cyan-100 text-cyan-800' },
  equipment_management_register: { label: 'Equipment Management Register', color: 'bg-lime-100 text-lime-800' },
  emergency_response_procedure: { label: 'Emergency Response Procedure', color: 'bg-red-100 text-red-800' },
};

// ---------------------------------------------------------------------------
// Main Page
// ---------------------------------------------------------------------------

export function GeneratedDocumentsPage() {
  const { data: documents, isLoading } = useGeneratedDocuments();
  const [selectedId, setSelectedId] = useState<number | null>(null);

  if (isLoading) {
    return (
      <div className="mx-auto max-w-7xl space-y-6 p-4 sm:p-6 lg:p-8">
        <Skeleton className="h-10 w-72" />
        <div className="flex gap-6">
          <div className="w-80 shrink-0 space-y-3">
            {Array.from({ length: 4 }).map((_, i) => (
              <Skeleton key={i} className="h-20" />
            ))}
          </div>
          <Skeleton className="h-96 flex-1" />
        </div>
      </div>
    );
  }

  const docs = documents || [];
  const selectedDoc = docs.find((d) => d.id === selectedId) || null;

  // Group by document type
  const grouped = docs.reduce<Record<string, GeneratedComplianceDocument[]>>(
    (acc, doc) => {
      const key = doc.document_type;
      if (!acc[key]) acc[key] = [];
      acc[key].push(doc);
      return acc;
    },
    {}
  );

  return (
    <div className="mx-auto max-w-7xl space-y-6 p-4 sm:p-6 lg:p-8">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Generated Documents</h1>
          <p className="mt-1 text-sm text-gray-500">
            {docs.length} compliance document{docs.length !== 1 ? 's' : ''} generated from your setup wizard profile
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" asChild>
            <Link to="/compliance/setup-wizard">
              <ArrowLeft className="mr-2 h-4 w-4" />
              Edit Profile
            </Link>
          </Button>
        </div>
      </div>

      {/* Empty state */}
      {docs.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-16 text-center">
            <FileText className="h-12 w-12 text-gray-300" />
            <h3 className="mt-4 text-lg font-semibold text-gray-700">No documents yet</h3>
            <p className="mt-2 max-w-sm text-sm text-gray-500">
              Complete the Setup Wizard to generate your compliance documents automatically.
            </p>
            <Button className="mt-6" asChild>
              <Link to="/compliance/setup-wizard">Start Setup Wizard</Link>
            </Button>
          </CardContent>
        </Card>
      ) : (
        /* List-detail layout */
        <div className="flex gap-6">
          {/* Left rail */}
          <div className="w-80 shrink-0 space-y-4 overflow-y-auto">
            {Object.entries(grouped).map(([docType, typeDocs]) => {
              const config = DOC_TYPE_CONFIG[docType] || {
                label: docType,
                color: 'bg-gray-100 text-gray-800',
              };
              return (
                <div key={docType}>
                  <h3 className="mb-2 text-xs font-semibold uppercase tracking-wider text-gray-500">
                    {config.label}
                  </h3>
                  <div className="space-y-2">
                    {typeDocs.map((doc) => (
                      <DocumentListItem
                        key={doc.id}
                        doc={doc}
                        isSelected={selectedId === doc.id}
                        onSelect={() => setSelectedId(doc.id)}
                      />
                    ))}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Right pane */}
          <div className="min-w-0 flex-1">
            {selectedDoc ? (
              <Card>
                <CardContent className="pt-6">
                  <GeneratedDocumentEditor key={selectedDoc.id} doc={selectedDoc} />
                </CardContent>
              </Card>
            ) : (
              <Card>
                <CardContent className="flex flex-col items-center justify-center py-24 text-center">
                  <FileText className="h-12 w-12 text-gray-300" />
                  <h3 className="mt-4 text-lg font-semibold text-gray-700">
                    Select a document
                  </h3>
                  <p className="mt-2 max-w-sm text-sm text-gray-500">
                    Choose a document from the list to view and edit its content.
                  </p>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Document List Item
// ---------------------------------------------------------------------------

function DocumentListItem({
  doc,
  isSelected,
  onSelect,
}: {
  doc: GeneratedComplianceDocument;
  isSelected: boolean;
  onSelect: () => void;
}) {
  const timeZone = useOrgTimeZone();
  return (
    <button
      onClick={onSelect}
      className={`w-full rounded-lg border p-3 text-left transition-colors ${
        isSelected
          ? 'border-blue-500 bg-blue-50'
          : 'border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50'
      }`}
    >
      <div className="flex items-start justify-between gap-2">
        <p className="text-sm font-medium text-gray-900 line-clamp-1">
          {doc.document_number}
        </p>
        <Badge
          variant={doc.is_draft ? 'secondary' : 'default'}
          className="shrink-0 text-[10px]"
        >
          {doc.is_draft ? 'Draft' : 'Published'}
        </Badge>
      </div>
      <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-gray-500">
        <span className="flex items-center gap-1">
          <Hash className="h-3 w-3" />
          v{doc.version}
        </span>
        <span className="flex items-center gap-1">
          <Calendar className="h-3 w-3" />
          {formatDateTimeInTimeZone(
            doc.last_edited_at || doc.updated_at,
            timeZone,
            { dateStyle: 'medium' }
          )}
        </span>
      </div>
    </button>
  );
}
